// 1. React imports

// 2. External libraries
import { BadgeCheck } from 'lucide-react';

// 3. Internal components
import { Badge } from '~/components/ui/badge';
import { Spinner } from '~/components/ui/spinner';

// 4. Type imports
import type { OAuthClientMetadata } from '~/api/oauth';

// 5. Custom hooks and utilities
import { CLIENT_TYPE_DISPLAY_NAMES, getConnectionTypeIcon } from '~/utils/connectionTypes';

/**
 * Props for ClientInfoSection component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface ClientInfoSectionProps {
  /** OAuth client ID requesting authorization, null if unknown */
  clientId: string | null;

  /** OAuth client type for display name lookup ('chatgpt' | 'claude'), null if unknown */
  clientType: string | null;

  /** OAuth client metadata fetched from backend, null if not available */
  metadata: OAuthClientMetadata | null;

  /** Loading state for metadata fetch */
  isLoading: boolean;

  /** Scopes requested by the OAuth client, rendered in plain language */
  requestedScopes?: string[];

  /** OAuth redirect URI; its host is shown so the user knows where they land after consent */
  redirectUri?: string | null;
}

/**
 * Maps a raw OAuth scope string (e.g. "read:messages") to a short, plain-language
 * description for display in the consent screen.
 */
function describeScope(scope: string): string {
  if (scope.startsWith('read')) {
    return 'Read your messages';
  }
  if (scope.startsWith('write')) {
    return 'Send messages on your behalf';
  }
  return scope;
}

function getRedirectHost(redirectUri: string | null): string | null {
  if (!redirectUri) return null;
  try {
    return new URL(redirectUri).host;
  } catch {
    return null;
  }
}

/**
 * Displays OAuth client information with metadata, trust badges, and warnings.
 *
 * Behavior:
 * - Displays loading spinner during metadata fetch
 * - Shows clientName from metadata if available
 * - Falls back to CLIENT_TYPE_DISPLAY_NAMES mapping using clientType
 * - Falls back to clientId if no clientType match found
 * - Displays logo from logoUri if provided
 * - Shows green "Verified" trust badge for registered clients (isRegistered === true)
 * - Renders requestedScopes in plain language and the redirect host, when provided
 *
 * Per §TS.3.1: Function declaration for named export.
 *
 * @example
 * ```tsx
 * // Loading state
 * <ClientInfoSection clientId="dd_client_123" clientType="chatgpt" metadata={null} isLoading={true} />
 *
 * // Registered client with metadata
 * <ClientInfoSection
 *   clientId="dd_client_123"
 *   clientType="chatgpt"
 *   metadata={{ clientName: "ChatGPT Teams", isRegistered: true, logoUri: "https://...", ... }}
 *   isLoading={false}
 * />
 *
 * // Unregistered client (warning shown)
 * <ClientInfoSection
 *   clientId="unknown-client"
 *   clientType={null}
 *   metadata={{ clientId: "unknown-client", isRegistered: false, ... }}
 *   isLoading={false}
 * />
 * ```
 */
export function ClientInfoSection({
  clientId,
  clientType,
  metadata,
  isLoading,
  requestedScopes = [],
  redirectUri = null,
}: ClientInfoSectionProps) {
  const redirectHost = getRedirectHost(redirectUri);
  // Determine display name with priority (per §BASIC.2.2):
  // 1. CLIENT_TYPE_DISPLAY_NAMES[clientType] (friendly name for known clients like "chatgpt" -> "ChatGPT")
  // 2. metadata.clientName (for custom/unknown clients)
  // 3. clientId (fallback to raw ID)
  // 4. 'Unknown Application' (ultimate fallback)
  const clientName =
    clientType && CLIENT_TYPE_DISPLAY_NAMES[clientType]
      ? CLIENT_TYPE_DISPLAY_NAMES[clientType]
      : metadata?.clientName && metadata.clientName.trim()
        ? metadata.clientName
        : clientId
          ? clientId
          : 'Unknown Application';

  // Per §TS.8.1: Early return for loading state
  if (isLoading) {
    return (
      <div className="mb-6 flex items-center gap-4">
        <Spinner data-testid="client-info-loading-spinner" />
        <p className="text-base text-muted-foreground">Loading client information...</p>
      </div>
    );
  }

  // Get icon for client type using getConnectionTypeIcon helper (per §BASIC.2.2)
  const clientIcon = clientType
    ? getConnectionTypeIcon(clientType, { className: 'size-10' })
    : null;

  return (
    <div>
      {/* Two-column layout: logo on left, content on right */}
      <div className="flex items-start gap-6">
        {/* Left column: Client logo */}
        {clientIcon && <div className="shrink-0">{clientIcon}</div>}

        {/* Right column: Content */}
        <div className="flex-1">
          {/* Title row with verified badge centered vertically */}
          <div className="mb-2 flex items-center justify-between">
            <h1 className="text-xl font-semibold">Authorize {clientName}</h1>

            {/* Trust badge for registered clients */}
            {metadata?.isRegistered && (
              <Badge variant="success">
                <BadgeCheck />
                Verified
              </Badge>
            )}
          </div>

          <p className="mb-2 text-base">
            <strong>{clientName}</strong> wants to connect to DeadDrop and is requesting the
            following permissions:
          </p>

          <ul className="mt-2 mb-0 list-disc pl-6">
            {requestedScopes.length > 0 ? (
              requestedScopes.map((scope) => (
                <li key={scope}>
                  <span className="text-sm">{describeScope(scope)}</span>
                </li>
              ))
            ) : (
              <li>
                <span className="text-sm">No specific permissions requested</span>
              </li>
            )}
          </ul>

          <p className="mt-2 text-sm text-muted-foreground">
            Access will be limited to the topic you select below.
            {redirectHost && <> You&rsquo;ll be redirected to {redirectHost}.</>}
          </p>
        </div>
      </div>
    </div>
  );
}
